import React, { useContext, useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  FlatList,
  ListRenderItem,
} from 'react-native'
import {
  BORDER_STYLES,
  COLORS,
  COMMON_STYLES,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { useNavigation } from '@react-navigation/native'


import RoundImageWithName from 'app/components/reusable/RoundImageWithName'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'

import fetchFarmerCrops from '../../apis/fetchFarmerCrops'
import Loader from './Loader'
import CropSelectionComponent from './AddCropModal'
import { Crop } from '../../models/crop'
import { trackEvent } from '../../utils/tracking'
import { Farmer360UserInformationAdded } from '../../utils/event'

interface Props {
  farmerId: string
  reload: () => void
  isEditAllowed: boolean
  highlight: boolean
  name: string
  phoneNumber: string
}

interface AddCropBtnProps {
  onClick: () => void
}

const AddCropButton = ({ onClick }: AddCropBtnProps) => (
  <Pressable onPress={onClick}>
    <View style={[styles.addCropBtn, styles.img100X100]}>
      <Text
        style={[
          TYPOGRAPHY.HEADING1,
          VARIANTS_STYLES.EMPHASIZED,
          TEXT_STYLES.textPrimary100,
        ]}
      >
        +
      </Text>
    </View>
    <Text
      style={[
        TYPOGRAPHY.HEADING3,
        VARIANTS_STYLES.MEDIUM,
        TEXT_STYLES.textPrimary100,
        COMMON_STYLES['mt1/2'],
      ]}
    >
      Add Crops
    </Text>
  </Pressable>
)

const Crops = ({
  farmerId,
  reload,
  isEditAllowed,
  highlight,
  name,
  phoneNumber,
}: Props) => {
  const isFocused = useNavigation()
  const [isLoading, setIsLoading] = useState(true)
  const [crops, setCrops] = useState<Crop[]>([])
  const [isAddCropModalVisible, setIsAddCropModalVisibility] = useState(false)

  const { mediaStyles } = useContext(ResponsiveContext)

  const fetchAndSetFarmerCrops = async () => {
    setIsLoading(true)
    const results = await fetchFarmerCrops(farmerId)
    if (results) {
      setCrops(results)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (isFocused) fetchAndSetFarmerCrops()
  }, [isFocused])

  const renderItem: ListRenderItem<Crop> = ({ item }) => {
    return (
      <RoundImageWithName
        name={item.name}
        imgUrl={item.image_url}
        handleItemClick={() => {}}
        key={item.id}
        imageStyle={[
          BORDER_STYLES.borderNeutral10,
          COMMON_STYLES.b1,
          styles.img100X100,
        ]}
        textStyle={[
          TYPOGRAPHY.HEADING3,
          VARIANTS_STYLES.MEDIUM,
          TEXT_STYLES.textNeutral100,
        ]}
        textNumberOfLines={2}
        style={mediaStyles({
          desktopStyle: styles['mr5/2'],
          mobileStyle: COMMON_STYLES.mr1,
        })}
      />
    )
  }

  return (
    <View
      style={[
        COMMON_STYLES.flex1,
        COMMON_STYLES.mv1,
        mediaStyles({ mobileStyle: COMMON_STYLES.ph1 }),
        highlight && [COMMON_STYLES.b1, BORDER_STYLES.borderPrimary100],
      ]}
    >
      <Text
        style={[
          TYPOGRAPHY.HEADING1,
          VARIANTS_STYLES.MEDIUM,
          TEXT_STYLES.textNeutral100,
        ]}
      >
        Crops
      </Text>
      {isLoading ? (
        <Loader />
      ) : (
        <FlatList
          horizontal
          data={crops}
          renderItem={renderItem}
          keyExtractor={(item, index) => item.id}
          contentContainerStyle={[
            COMMON_STYLES.row,
            COMMON_STYLES.mv1,
            mediaStyles({ desktopStyle: COMMON_STYLES.flex1 }),
          ]}
          showsHorizontalScrollIndicator={false}
          ListFooterComponent={
            <>
              {isEditAllowed && (
                <AddCropButton
                  onClick={() => setIsAddCropModalVisibility(true)}
                />
              )}
            </>
          }
        />
      )}
      {isAddCropModalVisible && (
        <CropSelectionComponent
          farmerId={farmerId}
          userCrops={crops}
          onClose={() => setIsAddCropModalVisibility(false)}
          onSubmit={(crops: string) => {
            trackEvent(Farmer360UserInformationAdded, {
              farmername: name,
              farmermobile: phoneNumber,
              InformationType: 'crops',
              InformationUpdated: crops,
            })
            reload()
          }}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  img100X100: {
    height: 100,
    width: 100,
    borderRadius: 50,
  },
  addCropBtn: {
    borderWidth: 1,
    borderColor: COLORS.primary100,
    backgroundColor: COLORS.primary20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  'mr5/2': {
    marginRight: 40,
  },
})

export default Crops
